import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ref, get, push, set as dbSet } from "firebase/database";
import { database } from "../firebaseConfig";
import { useActiveWorld } from "../hooks/useActiveWorld";
import {
  CLOUD_FUNCTION_URLS,
  authenticatedFetch,
} from "../utils/cloudFunctions";
import WorldDbHeader from "./WorldDbHeader";
import IntakeStep from "./OfferCreator/ValueBasedOffer/IntakeStep";
import Configurator from "./OfferCreator/ValueBasedOffer/Configurator";
import OfferView from "./OfferCreator/ValueBasedOffer/OfferView";
import GeneratingSkeleton from "./OfferCreator/ValueBasedOffer/GeneratingSkeleton";
import "./WorldDb.css";
import "./OfferCreator/ValueBasedOffer/ValueBasedOffer.css";

const DEFAULT_CONFIG = {
  companyName: "",
  // Primary contact / addressee on the offer letter. Used by the AI prompt
  // for the greeting and the "Danke für unser Gespräch"-line. Default = the
  // first entry of meetingContacts when extracted from the brain dump.
  invoiceRecipient: "",
  // ALL people we (BonusClub) actually spoke with during the discovery
  // call. The AI prompt references all of them when the greeting / intro
  // would otherwise read awkwardly with a single name. Source: brain-dump
  // intake. Editable as a comma-separated list in the configurator.
  meetingContacts: [],
  // Geschäftsführer / managing director from the company's Impressum.
  // Auto-filled by the Grok research step. NEVER used for greeting / intro
  // copy unless explicitly chosen as the addressee via `addresseeMode`.
  legalRepresentative: "",
  // Who the offer is formally addressed to. Drives the single z.Hd. line in
  // the letterhead AND the AI greeting.
  //   "meeting" — the person we actually spoke with (invoiceRecipient).
  //               Default — preferred whenever a brain-dump contact exists.
  //   "legal"   — the Geschäftsführer from the Impressum. Pick this only when
  //               the offer should land directly on the GF's desk (e.g. high-
  //               touch enterprise deals or when no meeting contact exists).
  addresseeMode: "meeting",
  websiteUrl: "",
  // Multi-line postal address as printed on the company's Impressum.
  // Auto-prefilled by the Grok research (researchCompanyForOffer); used as
  // the formal "Briefkopf" address on the rendered offer.
  companyAddress: "",
  currentSituation: "",
  situationOverview: "",
  campaignGoals: "",
  additionalInfo: "",
  conversationHistory: "",
  // Annual revenue per closed deal — drives the value-based pricing engine.
  // Range 1.000 € – 100.000 €. 10.000 € as a sensible default.
  revenuePerClose: 10000,
  // When true, the standard SalesGod prices (1.600/2.600/5.600) are slightly
  // adjusted by deal value and quality scores, and the AI is told about it.
  valueBasedPricingEnabled: false,
  // Manual override for the live revenue simulator (0–50). When set, every
  // pricing card uses this exact monthly appointment count instead of the
  // tier defaults (5 / 12 / 25). 0 means "use tier defaults".
  appointmentsPerMonth: 0,
  // Customer's gross profit margin in percent (10–100). Drives the per-appointment
  // bonus calculation: a higher-margin business has more profit per close, so we
  // capture more per appointment. Default 30 % is a typical B2B service margin.
  profitMargin: 30,
  // Average customer lifetime in years (1–20). Used for LTV calculations:
  // Kunde-Profit pro Lead = Verkaufswert × Marge × Lifetime-Jahre.
  // ROI für den Kunden ist dann LTV / CAC (cost per acquisition).
  customerLifetimeYears: 3,
  blueOceanScore: 5,
  uniquenessScore: 5,
  marketNeedScore: 5,
  onlinePresenceScore: 5,
  // Wie etabliert ist Cold Outreach in dieser Branche?
  // 0 = extrem schwer (stark reguliert, hohe Skepsis, persönliche Empfehlung Pflicht)
  // 10 = absolute Standard-Methode (B2B-SaaS, Agenturen, Recruiting, Beratung).
  coldOutreachFitScore: 5,
  spirit: "B",
  customerPriorities: [],
  // Which setup packages the salesperson wants to expose to the prospect in
  // the generated offer. The catalogue lives in Configurator.SETUP_PACKAGES.
  // Default: only "standard" (the in-month setup, free of charge). Sales can
  // additionally enable "premium" (3.240 €, optional add-on) per offer.
  setupPackages: ["standard"],
  // IDs of terms (from /World/<uid>/terms) to be auto-attached to the
  // generated offer. Pre-filled from the user's library when "enabledByDefault"
  // is set on a term; salesperson can refine in the Configurator.
  termIds: [],
  // Explicit monthly prices for the three options [Foundation, Plus, Premium],
  // taken verbatim from the brief at intake (or set directly). When present
  // these OVERRIDE the standard / value-based prices everywhere. null = use the
  // pricing engine. See generateValueBasedOffer.js + Configurator.computePricing.
  tierPriceOverrides: null,
  // Appointment bonus: billing starts at this appointment (default 4 = the
  // first 3 are included). 1 = every appointment is billed.
  bonusFromAppointment: 4,
  // Manual € per extra appointment. null = the value-based formula.
  appointmentPriceOverride: null,
  // Optional pre-selection of the tier the customer wants (0 = Foundation,
  // 1 = Plus, 2 = Premium). When set, the generated offer marks that option
  // with a check and greys out the others. null = no pre-selection.
  selectedTierIndex: null,
  // German address form for the generated offer copy: "Sie" (formal, default)
  // or "Du" (informal). Drives tone in the AI prompt.
  addressForm: "Sie",
  // DISC profile of the decision maker (optional). null = neutral / no nudge.
  // Possible values: "DOMINANT", "INITIATIV", "GEWISSENHAFT", "STETIG".
  // Drives a small style adjustment (~20 %) in the AI prompt.
  decisionMakerType: null,
};

// Strip undefined values so Firebase RTDB accepts the object.
const sanitize = (val) => {
  if (Array.isArray(val)) return val.map(sanitize);
  if (val && typeof val === "object") {
    const out = {};
    for (const k of Object.keys(val)) {
      const v = sanitize(val[k]);
      if (v !== undefined) out[k] = v;
    }
    return out;
  }
  return val === undefined ? null : val;
};

const ValueBasedOfferPage = () => {
  const navigate = useNavigate();
  const { offerId: routeOfferId } = useParams();

  // Value Offers, Base Sets und die Terms-Bibliothek gehören der aktiven
  // World. Team-Member mit Full Access arbeiten damit in der World des
  // Owners statt in ihrer eigenen.
  const {
    userId,
    isLoading: isWorldLoading,
    isInitialized: isWorldInitialized,
    isSwitching,
  } = useActiveWorld();
  const authReady = isWorldInitialized && !isWorldLoading && !isSwitching;

  // mode: "intake" | "configurator" | "generating" | "offer"
  const [mode, setMode] = useState(routeOfferId ? "configurator" : "intake");
  const [config, setConfig] = useState(DEFAULT_CONFIG);
  const [baseSets, setBaseSets] = useState([]);
  const [baseSetId, setBaseSetId] = useState(null);
  const [offer, setOffer] = useState(null); // { id, config, baseSetId, generated, createdAt, updatedAt }
  const [errors, setErrors] = useState({});
  const [isGenerating, setIsGenerating] = useState(false);
  const [isPreparing, setIsPreparing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSharing, setIsSharing] = useState(false);
  const [publicUrl, setPublicUrl] = useState(null);
  const [notice, setNotice] = useState(null);
  const [intakeText, setIntakeText] = useState("");
  const [filledFields, setFilledFields] = useState([]);
  const [isResearching, setIsResearching] = useState(false);
  const [scoreReasoning, setScoreReasoning] = useState({});
  const [researchInfo, setResearchInfo] = useState(null);

  // Terms library — pulled from /World/<uid>/terms. Used in two places:
  //  1. Configurator: multi-select picker so the salesperson chooses which
  //     terms to auto-attach to this specific offer.
  //  2. OfferView: "+ Term aus Bibliothek" picker for ad-hoc additions
  //     after the offer has been generated.
  const [termsLibrary, setTermsLibrary] = useState([]);

  // Skeleton crossfade state. While "exiting" the skeleton stays mounted as an
  // absolutely-positioned overlay so the new OfferView can fade in underneath
  // without any layout jolt.
  // "hidden" | "visible" | "exiting"
  const [skeletonPhase, setSkeletonPhase] = useState("hidden");

  // Sync mode → skeletonPhase (mount, exit, unmount) and gently scroll to the
  // top whenever the user transitions into the generating/offer view, so the
  // skeleton + final offer always start at a predictable scroll origin.
  useEffect(() => {
    if (mode === "generating") {
      setSkeletonPhase("visible");
      try {
        window.scrollTo({ top: 0, behavior: "smooth" });
      } catch (_) {
        window.scrollTo(0, 0);
      }
      return undefined;
    }
    if (skeletonPhase === "visible") {
      // Generation finished — start crossfade.
      setSkeletonPhase("exiting");
      // Pin scroll to the top exactly once so the OfferView's first paint sits
      // where the skeleton was. Smooth-scroll would race the fade.
      window.scrollTo(0, 0);
      const t = setTimeout(() => setSkeletonPhase("hidden"), 520);
      return () => clearTimeout(t);
    }
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  const autoResearchTriggeredRef = useRef(false);

  // ---------------- World switch ----------------
  // Wechselt der User über den TeamSwitcher im Header den Account, gehören
  // Config und offer.id noch zur alten World. Ein Auto-Save würde sie unter
  // derselben ID in die neue World schreiben — daher hart zurücksetzen.
  // Muss vor allen Lade-Effekten stehen, die ebenfalls an `userId` hängen.
  const previousWorldIdRef = useRef(null);
  useEffect(() => {
    const previousWorldId = previousWorldIdRef.current;
    previousWorldIdRef.current = userId;
    if (!previousWorldId || previousWorldId === userId) return;

    setOffer(null);
    setConfig(DEFAULT_CONFIG);
    setBaseSetId(null);
    setBaseSets([]);
    setTermsLibrary([]);
    setPublicUrl(null);
    setFilledFields([]);
    setScoreReasoning({});
    setResearchInfo(null);
    setIntakeText("");
    setErrors({});
    autoResearchTriggeredRef.current = false;
    setMode("intake");
    if (routeOfferId) navigate("/value-offer", { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  // ---------------- Load base sets (from settings) ----------------
  useEffect(() => {
    const load = async () => {
      if (!userId) return;
      try {
        const snap = await get(
          ref(database, `/World/${userId}/valueBasedOffers`)
        );
        if (snap.exists()) {
          const list = [];
          snap.forEach((child) => {
            list.push({ id: child.key, ...child.val() });
          });
          list.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
          setBaseSets(list);
        } else {
          setBaseSets([]);
        }
      } catch (e) {
        console.error("Error loading base value-based sets:", e);
      }
    };
    load();
  }, [userId]);

  // ---------------- Load terms library ----------------
  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    (async () => {
      try {
        const snap = await get(ref(database, `/World/${userId}/terms`));
        if (cancelled) return;
        if (!snap.exists()) {
          setTermsLibrary([]);
          return;
        }
        const list = [];
        snap.forEach((child) => {
          list.push({ id: child.key, ...child.val() });
        });
        list.sort(
          (a, b) =>
            (a.order ?? 9999) - (b.order ?? 9999) ||
            (a.createdAt || 0) - (b.createdAt || 0)
        );
        setTermsLibrary(list);

        // Pre-select all default-enabled terms on first load (only if the
        // user hasn't picked their own selection yet — i.e. termIds is the
        // initial empty array). Don't override an existing offer's choice.
        setConfig((cur) => {
          const hasUserSelection = Array.isArray(cur.termIds) && cur.termIds.length > 0;
          if (hasUserSelection) return cur;
          if (routeOfferId) return cur; // editing an existing offer — leave as is
          const defaults = list
            .filter((t) => t.enabledByDefault !== false)
            .map((t) => t.id);
          return { ...cur, termIds: defaults };
        });
      } catch (e) {
        console.error("ValueBasedOfferPage: load terms library failed", e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [userId, routeOfferId]);

  // ---------------- Load existing offer from URL ----------------
  useEffect(() => {
    const load = async () => {
      if (!userId || !routeOfferId) return;
      setIsLoading(true);
      try {
        const snap = await get(
          ref(database, `/World/${userId}/valueOffers/${routeOfferId}`)
        );
        if (snap.exists()) {
          const data = { id: routeOfferId, ...snap.val() };
          setOffer(data);
          setConfig({ ...DEFAULT_CONFIG, ...(data.config || {}) });
          setBaseSetId(data.baseSetId || null);
          setMode(data.generated ? "offer" : "configurator");
        } else {
          showNotice("Offer nicht gefunden", "error");
          navigate("/value-offer");
        }
      } catch (e) {
        console.error("Error loading offer:", e);
        showNotice("Fehler beim Laden", "error");
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, [userId, routeOfferId, navigate]);

  // ---------------- Helpers ----------------
  const showNotice = (message, type = "info") => {
    setNotice({ message, type });
    setTimeout(() => setNotice(null), 4000);
  };

  // ---------------- Intake (raw text → AI-extracted config) ----------------
  const handlePrepareIntake = async (rawText) => {
    if (!rawText || rawText.trim().length < 20) return;
    setIntakeText(rawText);
    setIsPreparing(true);
    try {
      const resp = await authenticatedFetch(
        CLOUD_FUNCTION_URLS.prepareValueBasedOfferIntake(),
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ rawText, userId }),
        }
      );
      if (!resp.ok) {
        const errBody = await resp.json().catch(() => ({}));
        // `details` carries the actionable text (e.g. how many credits are
        // missing); `error` alone is just a category like "Insufficient credits".
        throw new Error(errBody.details || errBody.error || `HTTP ${resp.status}`);
      }
      const data = await resp.json();
      const extracted = data.config || {};
      const filled = Array.isArray(data.filledFields)
        ? data.filledFields
        : Object.keys(extracted);

      // Merge into the existing config (extracted values win for filled keys)
      setConfig((cur) => ({ ...DEFAULT_CONFIG, ...cur, ...extracted }));
      setFilledFields(filled);
      setMode("configurator");
      showNotice(
        filled.length
          ? `${filled.length} Feld${filled.length === 1 ? "" : "er"} aus Brain-Dump übernommen`
          : "Konnte nichts Belastbares extrahieren — bitte manuell ausfüllen",
        filled.length ? "success" : "info"
      );
    } catch (e) {
      console.error("prepareValueBasedOfferIntake failed:", e);
      showNotice(`Aufbereitung fehlgeschlagen: ${e.message}`, "error");
    } finally {
      setIsPreparing(false);
    }
  };

  const handleSkipIntake = () => {
    setMode("configurator");
    setFilledFields([]);
  };

  const handleBackToIntake = () => setMode("intake");

  // ---------------- Auto-Research (Grok web search + Claude scoring) ----------------
  const handleResearch = async () => {
    const company = (config.companyName || "").trim();
    if (!company) {
      showNotice("Trage zuerst den Firmennamen ein", "error");
      return;
    }
    setIsResearching(true);
    try {
      const resp = await authenticatedFetch(
        CLOUD_FUNCTION_URLS.researchCompanyForOffer(),
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            companyName: company,
            websiteUrl: (config.websiteUrl || "").trim(),
            additionalContext: [
              config.currentSituation,
              config.situationOverview,
              config.additionalInfo,
            ]
              .filter(Boolean)
              .join("\n\n")
              .slice(0, 1800),
            userId,
          }),
        }
      );
      if (!resp.ok) {
        const errBody = await resp.json().catch(() => ({}));
        // `details` carries the actionable text (e.g. how many credits are
        // missing); `error` alone is just a category like "Insufficient credits".
        throw new Error(errBody.details || errBody.error || `HTTP ${resp.status}`);
      }
      const data = await resp.json();
      const extracted = data.config || {};
      const reasoning = data.reasoning || {};
      const filled = Array.isArray(data.filledFields)
        ? data.filledFields
        : Object.keys(extracted);

      setConfig((cur) => ({ ...cur, ...extracted }));
      setFilledFields((cur) =>
        Array.from(new Set([...(cur || []), ...filled]))
      );
      setScoreReasoning((cur) => ({ ...cur, ...reasoning }));
      setResearchInfo({
        ...(data.research || {}),
        citations: data.citations || [],
      });
      showNotice(
        filled.length
          ? `${filled.length} Score${filled.length === 1 ? "" : "s"} aus Web-Recherche übernommen`
          : "Recherche lieferte keine belastbaren Werte",
        filled.length ? "success" : "info"
      );
    } catch (e) {
      console.error("researchCompanyForOffer failed:", e);
      showNotice(`Recherche fehlgeschlagen: ${e.message}`, "error");
    } finally {
      setIsResearching(false);
    }
  };

  // Auto-trigger Grok web research as soon as the configurator is mounted with
  // a company name in the config — typically right after step 1 (intake) has
  // populated companyName from the brain dump. This runs in the background so
  // the salesperson sees the spinner while filling out the rest of the form,
  // and lands on a page where Branche, Brand, Wettbewerb & Cold-Outreach-Fit
  // are already pre-scored from the web search.
  //
  // Guards:
  //   - only fires once per mount (autoResearchTriggeredRef, oben deklariert
  //     weil der World-Switch-Reset ihn ebenfalls zurücksetzt)
  //   - skips if research already produced data (e.g. reloaded saved offer)
  //   - skips if we're already researching
  useEffect(() => {
    if (mode !== "configurator") return;
    if (autoResearchTriggeredRef.current) return;
    if (!config.companyName?.trim()) return;
    if (isResearching) return;
    if (researchInfo) return;
    autoResearchTriggeredRef.current = true;
    handleResearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, config.companyName, isResearching, researchInfo]);

  const validateConfig = (cfg = config) => {
    const e = {};
    if (!cfg.companyName?.trim()) e.companyName = "Pflichtfeld";
    if (!cfg.invoiceRecipient?.trim()) e.invoiceRecipient = "Pflichtfeld";
    const rev = Number(cfg.revenuePerClose);
    if (!Number.isFinite(rev) || rev < 1000) e.revenuePerClose = "Pflichtfeld";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  // ---------------- Generate ----------------
  // `configOverride` is the configurator's latest snapshot, including a price
  // edit that was committed in the same click as Generate. React state may
  // not have flushed yet, so the request must use this object.
  const handleGenerate = async (configOverride) => {
    const activeConfig =
      configOverride &&
      typeof configOverride === "object" &&
      typeof configOverride.companyName === "string"
        ? configOverride
        : config;
    if (!validateConfig(activeConfig)) {
      showNotice("Bitte Pflichtfelder ausfüllen", "error");
      return;
    }
    // Hard-block: never generate while the Grok research is still running —
    // otherwise the offer ships without companyAddress / legalRepresentative
    // and the customer letter renders the placeholder address. The user has
    // to wait the ~60-90s for the impressum/market lookup to land.
    if (isResearching) {
      showNotice(
        "Recherche läuft noch (~60 Sek.) — bitte kurz warten, damit Adresse & Geschäftsführer im Angebot landen.",
        "info"
      );
      return;
    }
    setIsGenerating(true);
    setMode("generating");
    try {
      const resp = await authenticatedFetch(
        CLOUD_FUNCTION_URLS.generateValueBasedOffer(),
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          // `userId` = aktive World. Der Server lädt Base Set und Terms
          // daraus, nachdem er die Team-Berechtigung geprüft hat.
          body: JSON.stringify({ config: activeConfig, baseSetId, userId }),
        }
      );
      if (!resp.ok) {
        const errBody = await resp.json().catch(() => ({}));
        // `details` carries the actionable text (e.g. how many credits are
        // missing); `error` alone is just a category like "Insufficient credits".
        throw new Error(errBody.details || errBody.error || `HTTP ${resp.status}`);
      }
      const data = await resp.json();
      const generated = data.generated;
      if (!generated) throw new Error("No generated content");

      const next = {
        ...(offer || {}),
        id: offer?.id,
        config: { ...activeConfig },
        baseSetId,
        generated,
        updatedAt: Date.now(),
        createdAt: offer?.createdAt || Date.now(),
        modelInfo: { model: data.model, usage: data.usage || null },
      };
      setOffer(next);
      setMode("offer");

      // auto-save the freshly generated offer
      await persist(next, { silent: true });
      showNotice("Angebot generiert ✨", "success");
    } catch (e) {
      console.error("generateValueBasedOffer failed:", e);
      showNotice(`Fehler: ${e.message}`, "error");
      setMode("configurator");
    } finally {
      setIsGenerating(false);
    }
  };

  // ---------------- Persist ----------------
  // Make sure `generated.recipient` is populated from the configurator
  // (companyName / invoiceRecipient / companyAddress) before we save or
  // share. The Anschriftenfeld is critical for the customer-facing letter
  // header — if the salesperson never manually clicks any of those inline
  // fields, `generated.recipient` would otherwise stay empty and the public
  // share would render without the recipient block.
  const withRecipientSnapshot = (data) => {
    if (!data || !data.generated) return data;
    const cfg = data.config || {};
    const existing = data.generated.recipient || {};
    // Existing meetingContacts on `generated.recipient` may already be an
    // array (when the cloud function snapshotted them at generation time)
    // or absent. Falling back to the configurator config keeps the public
    // share consistent with what the salesperson edited inline.
    const existingMeetingContacts = Array.isArray(existing.meetingContacts)
      ? existing.meetingContacts
      : null;
    const cfgMeetingContacts = Array.isArray(cfg.meetingContacts)
      ? cfg.meetingContacts
      : [];
    const recipient = {
      companyName: existing.companyName || cfg.companyName || "",
      invoiceRecipient: existing.invoiceRecipient || cfg.invoiceRecipient || "",
      companyAddress: existing.companyAddress || cfg.companyAddress || "",
      legalRepresentative:
        existing.legalRepresentative || cfg.legalRepresentative || "",
      meetingContacts: existingMeetingContacts || cfgMeetingContacts,
      // "meeting" (Hauptansprechpartner) | "legal" (Geschäftsführer).
      // Snapshotting this lets the public view render the correct single
      // z.Hd. line even after the salesperson edits the configurator.
      addresseeMode:
        existing.addresseeMode || cfg.addresseeMode || "meeting",
    };
    return { ...data, generated: { ...data.generated, recipient } };
  };

  const persist = async (data, { silent = false } = {}) => {
    if (!userId || !data) return;
    setIsSaving(true);
    try {
      const enriched = withRecipientSnapshot(data);
      const payload = sanitize({
        config: enriched.config,
        baseSetId: enriched.baseSetId,
        generated: enriched.generated,
        modelInfo: enriched.modelInfo || null,
        createdAt: enriched.createdAt || Date.now(),
        updatedAt: Date.now(),
      });

      if (data.id) {
        await dbSet(
          ref(database, `/World/${userId}/valueOffers/${data.id}`),
          payload
        );
      } else {
        const listRef = ref(database, `/World/${userId}/valueOffers`);
        const newRef = push(listRef);
        await dbSet(newRef, payload);
        const newId = newRef.key;
        setOffer((cur) => (cur ? { ...cur, id: newId } : { ...data, id: newId }));
        // navigate to the persistent URL so refreshing keeps the state
        navigate(`/value-offer/${newId}`, { replace: true });
      }
      if (!silent) showNotice("Gespeichert", "success");
    } catch (e) {
      console.error("Error saving value offer:", e);
      if (!silent) showNotice("Fehler beim Speichern", "error");
    } finally {
      setIsSaving(false);
    }
  };

  const handleSave = () => offer && persist(offer);

  // ---------------- Auto-save ----------------
  // Once the offer has an ID (= it's been persisted at least once after
  // generation), every subsequent in-memory edit silently saves to RTDB
  // after ~1.2s of inactivity. This removes the need for the salesperson
  // to remember to click "Speichern" after inline-edits in OfferView.
  //
  // We compare a stable JSON hash of (generated, config, baseSetId) against
  // the last-saved hash to avoid re-saving on no-op state changes (e.g.
  // setOffer-after-persist when the new id is assigned). The first run
  // simply seeds the hash so a freshly-loaded offer never triggers a save.
  const lastSavedHashRef = useRef(null);
  const autoSaveTimerRef = useRef(null);
  useEffect(() => {
    if (mode !== "offer") return undefined;
    if (!offer || !offer.id) return undefined;
    const hash = JSON.stringify({
      generated: offer.generated || null,
      config: offer.config || null,
      baseSetId: offer.baseSetId || null,
    });
    if (lastSavedHashRef.current === null) {
      lastSavedHashRef.current = hash;
      return undefined;
    }
    if (lastSavedHashRef.current === hash) return undefined;
    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    autoSaveTimerRef.current = setTimeout(() => {
      lastSavedHashRef.current = hash;
      persist(offer, { silent: true });
    }, 1200);
    return () => {
      if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offer, mode]);

  // Reset auto-save bookkeeping whenever we leave / re-enter the offer view
  // (e.g. user navigates back to configurator and re-generates).
  useEffect(() => {
    if (mode !== "offer") {
      lastSavedHashRef.current = null;
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
        autoSaveTimerRef.current = null;
      }
    }
  }, [mode]);

  const handleBackToConfig = () => setMode("configurator");

  // ---------------- Share (publish to /publicValueOffers and copy link) ----------------
  const handleShare = async () => {
    if (!userId || !offer || !offer.id) {
      showNotice("Bitte erst speichern, dann teilen", "info");
      return;
    }
    setIsSharing(true);
    try {
      // Make sure the latest in-memory state is also persisted before sharing
      const offerForShare = withRecipientSnapshot(offer);
      await persist(offerForShare, { silent: true });

      const customId = `${userId}+${offer.id}`;
      const publicPayload = sanitize({
        userId,
        offerId: offer.id,
        generated: offerForShare.generated,
        // Intentionally do NOT publish the raw configurator (internal notes,
        // scores, conversation history) — only the customer-facing offer.
        createdAt: offer.createdAt || Date.now(),
        updatedAt: Date.now(),
        publishedAt: Date.now(),
      });
      await dbSet(
        ref(database, `/publicValueOffers/${customId}`),
        publicPayload
      );

      const url = `${window.location.origin}/v/${customId}`;
      setPublicUrl(url);
      try {
        await navigator.clipboard.writeText(url);
        showNotice("Link kopiert · " + url, "success");
      } catch (_) {
        // Clipboard might be blocked (older browsers / non-https). Show URL so
        // the user can copy manually.
        showNotice("Geteilt: " + url, "success");
      }
    } catch (e) {
      console.error("Error sharing value offer:", e);
      showNotice("Fehler beim Erstellen des Share-Links", "error");
    } finally {
      setIsSharing(false);
    }
  };

  // ---------------- Render ----------------
  if (!authReady || isLoading) {
    return (
      <div className="worldDbMain">
        <WorldDbHeader activeView="valueOffer" setActiveView={() => {}} />
        <div className="vbo-page__loading">Lade…</div>
      </div>
    );
  }

  if (!userId) {
    return (
      <div className="worldDbMain">
        <WorldDbHeader activeView="valueOffer" setActiveView={() => {}} />
        <div className="vbo-page__loading">Bitte einloggen.</div>
      </div>
    );
  }

  return (
    <div className="worldDbMain">
      <WorldDbHeader activeView="valueOffer" setActiveView={() => {}} />
      <div className="vbo-page">
        {notice && (
          <div className={`vbo-page__notice vbo-page__notice--${notice.type}`}>
            {notice.message}
          </div>
        )}

        {(mode === "generating" || mode === "offer") && (
          <div className="vbo-mode-stage">
            {skeletonPhase !== "hidden" && (
              <div
                className={`vbo-mode-stage__skeleton ${
                  skeletonPhase === "exiting" ? "is-exiting" : ""
                }`}
                aria-hidden={skeletonPhase === "exiting" ? "true" : undefined}
              >
                <GeneratingSkeleton />
              </div>
            )}
            {mode === "offer" && offer && (
              <div className="vbo-mode-stage__offer is-entering">
                <OfferView
                  offer={offer}
                  onChange={setOffer}
                  onSave={handleSave}
                  onBackToConfig={handleBackToConfig}
                  onShare={handleShare}
                  isSaving={isSaving}
                  isSharing={isSharing}
                  publicUrl={publicUrl}
                  userId={userId}
                  termsLibrary={termsLibrary}
                />
              </div>
            )}
          </div>
        )}

        {mode === "intake" && (
          <IntakeStep
            initialText={intakeText}
            isPreparing={isPreparing}
            lastFilledFields={filledFields.length ? filledFields : null}
            onPrepare={handlePrepareIntake}
            onSkip={handleSkipIntake}
          />
        )}

        {mode === "configurator" && (
          <Configurator
            config={config}
            onChange={(next) => {
              setConfig(next);
              // when user manually edits a prefilled field, keep the dot but
              // don't break — we'll just leave filledFields as-is. (User edits
              // are still considered "from intake" since they originated from it.)
            }}
            baseSets={baseSets}
            baseSetId={baseSetId}
            onBaseSetChange={setBaseSetId}
            onGenerate={handleGenerate}
            isGenerating={isGenerating}
            errors={errors}
            filledFields={filledFields}
            onBackToIntake={!routeOfferId ? handleBackToIntake : null}
            onResearch={handleResearch}
            isResearching={isResearching}
            scoreReasoning={scoreReasoning}
            researchInfo={researchInfo}
            termsLibrary={termsLibrary}
          />
        )}

      </div>
    </div>
  );
};

export default ValueBasedOfferPage;
